import { useEffect } from 'react';
import { useParams, Link } from 'wouter';
import {
  useGetSite,
  useListScans,
  getGetSiteQueryKey,
  getListScansQueryKey,
} from '@workspace/api-client-react';
import { format } from 'date-fns';
import {
  ArrowLeft, Printer, AlertCircle, Info, FileText, Search,
  Activity, AlertTriangle, Ear, Keyboard,
  Eye, Layout, TextCursorInput, CheckCircle2,
  XCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const CATEGORY_ORDER = ['screen-reader', 'keyboard', 'visual', 'structure', 'forms'] as const;
type Category = typeof CATEGORY_ORDER[number];

const CATEGORY_LABELS: Record<Category, string> = {
  'screen-reader': 'Screen Reader',
  keyboard: 'Keyboard Navigation',
  visual: 'Visual Accessibility',
  structure: 'Page Structure',
  forms: 'Forms & Inputs',
};

const CATEGORY_ICONS: Record<Category, React.ElementType> = {
  'screen-reader': Ear,
  keyboard: Keyboard,
  visual: Eye,
  structure: Layout,
  forms: TextCursorInput,
};

const CATEGORY_EXPLAINERS: Record<Category, string> = {
  'screen-reader': 'Screen readers announce the content of the screen to visually impaired users. Issues in this category mean that screen readers may not be able to correctly interpret or announce your website\'s content.',
  keyboard: 'Keyboard navigation is essential for users who cannot use a mouse. Issues here mean that users might get trapped, skip essential content, or be unable to interact with forms and buttons.',
  visual: 'Visual accessibility relates to color contrast, text sizing, and motion. Issues here can make it difficult or impossible for visually impaired or colorblind users to read your content.',
  structure: 'A logical page structure using landmarks and headings helps all users, especially those using assistive technologies, to navigate and understand the relationship between different parts of the page.',
  forms: 'Accessible forms ensure that inputs, labels, and error messages are clearly associated so users know what information is expected and can successfully complete tasks.',
};

const SEVERITY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  critical:  { bg: '#fef2f2', text: '#b91c1c', border: '#fca5a5' },
  serious:   { bg: '#fff7ed', text: '#c2410c', border: '#fdba74' },
  moderate:  { bg: '#fefce8', text: '#854d0e', border: '#fde047' },
  minor:     { bg: '#f8fafc', text: '#475569', border: '#cbd5e1' },
};

const SEVERITY_LABELS: Record<string, string> = {
  critical: 'Critical',
  serious: 'Serious',
  moderate: 'Moderate',
  minor: 'Minor',
};

const SEVERITY_ORDER = ['critical', 'serious', 'moderate', 'minor'] as const;

const DISCLAIMER =
  'This report documents the results of an automated accessibility scan performed by ADA Widget. ' +
  'It is provided to help you improve accessibility and work toward WCAG best practices. ' +
  'It does not constitute a legal opinion, compliance certification, or guarantee of conformance with any law or regulation.';

function ADAWidgetLogo({ size = 32 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ borderRadius: 6, flexShrink: 0 }}>
      <rect width="48" height="48" rx="8" fill="url(#grad)" />
      <path d="M24 10C26.2091 10 28 11.7909 28 14C28 16.2091 26.2091 18 24 18C21.7909 18 20 16.2091 20 14C20 11.7909 21.7909 10 24 10Z" fill="white"/>
      <path d="M34 22H14C12.8954 22 12 21.1046 12 20C12 18.8954 12.8954 18 14 18H34C35.1046 18 36 18.8954 36 20C36 21.1046 35.1046 22 34 22Z" fill="white"/>
      <path d="M26 22V36C26 37.1046 25.1046 38 24 38C22.8954 38 22 37.1046 22 36V22H26Z" fill="white"/>
      <path d="M16 38C16 39.1046 15.1046 40 14 40C12.8954 40 12 39.1046 12 38L16 22H20L16 38Z" fill="white"/>
      <path d="M32 38C32 39.1046 32.8954 40 34 40C35.1046 40 36 39.1046 36 38L32 22H28L32 38Z" fill="white"/>
      <defs>
        <linearGradient id="grad" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
          <stop stopColor="hsl(215, 25%, 27%)"/>
          <stop offset="1" stopColor="hsl(24, 90%, 48%)"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

export default function ScanReportPage() {
  const params = useParams<{ siteId: string; scanId: string }>();
  const siteId = params.siteId ? Number(params.siteId) : null;
  const scanId = params.scanId ? Number(params.scanId) : null;

  const { data: site, isLoading: siteLoading } = useGetSite(siteId!, {
    query: { enabled: !!siteId, queryKey: getGetSiteQueryKey(siteId!) },
  });
  const { data: scans, isLoading: scansLoading } = useListScans(siteId!, {
    query: { enabled: !!siteId, queryKey: getListScansQueryKey(siteId!) },
  });

  const scan = scans?.find((s) => s.id === scanId) ?? null;
  const isLoading = siteLoading || scansLoading;

  useEffect(() => {
    if (!site || !scan) return;
    const dateStr = format(new Date(scan.createdAt), 'yyyy-MM-dd');
    const prev = document.title;
    document.title = `ADA Widget Report — ${site.name} — ${dateStr}`;
    return () => { document.title = prev; };
  }, [site, scan]);

  const handlePrint = () => window.print();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse space-y-4 w-full max-w-3xl p-8">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (!site || !scan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4 max-w-md p-8">
          <AlertCircle className="w-12 h-12 text-muted-foreground/40 mx-auto" />
          <h1 className="text-xl font-semibold text-foreground">Report not found</h1>
          <p className="text-muted-foreground text-sm">
            {!site ? 'The site could not be found.' : 'The scan could not be found for this site.'}
          </p>
          <Link href={siteId ? `/sites/${siteId}` : '/sites'}>
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const scanDate = format(new Date(scan.createdAt), 'MMMM d, yyyy');
  const scanTime = format(new Date(scan.createdAt), 'h:mm a');
  const findings = scan.findings ?? [];

  const findingsByCategory = Object.fromEntries(
    CATEGORY_ORDER.map((cat) => [
      cat,
      findings.filter((f) => (f.category ?? 'structure') === cat),
    ])
  ) as Record<Category, typeof findings>;

  const sortedFindings = CATEGORY_ORDER.flatMap(cat => findingsByCategory[cat] || []);
  const totalInstances = findings.reduce((sum, finding) => sum + finding.count, 0);
  const maxCategoryCount = Math.max(
    1,
    ...CATEGORY_ORDER.map((cat) => findingsByCategory[cat]?.length ?? 0),
  );
  const hasFindings = findings.length > 0;
  const isFailed = scan.status === 'failed';

  return (
    <div className="min-h-screen bg-[#eef3f9] font-sans text-foreground">
      <style>{`
        @media print {
          @page { size: A4; margin: 15mm 15mm 18mm; }
          .no-print { display: none !important; }
          .print-break-inside-avoid { break-inside: avoid; }
          .print-break-after-page { break-after: page; page-break-after: always; }
          html, body, #root {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            background: white !important;
            color: black !important;
          }
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print-footer {
            display: flex !important;
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            background: white;
            padding: 10px 0;
            border-top: 1px solid #e2e8f0;
          }
          .report-body {
            max-width: none !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            padding-bottom: 40px;
          }
          .report-page {
            --background: 0 0% 100%;
            --foreground: 222 47% 11%;
            --card: 0 0% 100%;
            --card-foreground: 222 47% 11%;
            --muted: 215 25% 96%;
            --muted-foreground: 215 25% 40%;
            --secondary: 215 25% 96%;
            --secondary-foreground: 222 47% 11%;
            --border: 215 25% 90%;
            --destructive: 0 84% 60%;
            min-height: calc(297mm - 33mm);
            margin: 0 !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            border: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            background: white !important;
            color-scheme: light;
          }
          .report-page:last-child {
            break-after: auto !important;
            page-break-after: auto !important;
          }
        }
        @media screen {
          .print-footer { display: none !important; }
          .report-page {
            --background: 0 0% 100%;
            --foreground: 222 47% 11%;
            --card: 0 0% 100%;
            --card-foreground: 222 47% 11%;
            --muted: 215 25% 96%;
            --muted-foreground: 215 25% 40%;
            --secondary: 215 25% 96%;
            --secondary-foreground: 222 47% 11%;
            --border: 215 25% 90%;
            --destructive: 0 84% 60%;
            margin: 24px 0;
            overflow: hidden;
            border: 1px solid #dbe4ee;
            border-radius: 20px;
            background: white;
            color: hsl(var(--foreground));
            box-shadow: 0 22px 60px -34px rgba(15, 35, 65, 0.34);
            color-scheme: light;
          }
        }
      `}</style>

      {/* Screen toolbar */}
      <div className="no-print sticky top-0 z-50 bg-white/95 text-slate-700 backdrop-blur border-b border-slate-200 px-6 py-4 flex items-center justify-between shadow-sm">
        <Link href={`/sites/${siteId}?tab=scans`} className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-testid="link-back-scans">
          <ArrowLeft className="w-4 h-4" />
          Back to scans
        </Link>
        <div className="flex items-center gap-4">
           <Button onClick={handlePrint} className="gap-2 rounded-lg" size="sm" data-testid="button-print">
             <Printer className="w-4 h-4" />
             Print / PDF
           </Button>
        </div>
      </div>

      <div className="report-body max-w-4xl mx-auto px-4 md:px-6 pb-24">
         {/* Cover Page */}
          <div id="report-cover" className="report-page print-break-after-page min-h-[80vh] flex flex-col justify-center px-6 py-20 md:px-14">
            <div className="mb-12">
               <ADAWidgetLogo size={56} />
            </div>
            <h1 className="text-5xl md:text-7xl font-light tracking-tight text-foreground mb-6" data-testid="text-report-title">
               Accessibility<br/>Remediation Report
            </h1>
            <div className="text-2xl text-muted-foreground font-light mb-16" data-testid="text-site-domain">
               {site.domain}
            </div>
             <p className="max-w-2xl text-base leading-relaxed text-muted-foreground">
                A prioritized snapshot of automated accessibility findings, their user impact,
                and practical guidance for remediation.
             </p>
             {isFailed && (
               <div className="mt-8 max-w-2xl rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm font-medium text-red-800">
                 This scan did not complete. The report records the failed attempt and does not include accessibility findings.
               </div>
             )}
            <div className="mt-auto">
                <div className="text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-2">
                  {isFailed ? 'Scan Attempted' : 'Scan Completed'}
                </div>
               <div className="text-lg text-foreground font-medium" data-testid="text-scan-date">{scanDate} at {scanTime}</div>
            </div>
         </div>

         {/* Scope & Methodology Page */}
          <div id="report-scope" className="report-page print-break-after-page px-6 py-16 md:px-14 md:py-24">
            <h2 className="text-2xl md:text-3xl font-light tracking-tight mb-12 border-b pb-6">Report Scope & Methodology</h2>

            <div className="grid gap-12 md:grid-cols-3">
               <section>
                  <FileText className="w-7 h-7 text-muted-foreground mb-5" />
                   <h3 className="text-lg font-medium mb-3">{isFailed ? 'URL targeted' : 'Pages scanned'}</h3>
                   <p className="text-muted-foreground leading-relaxed">
                     {isFailed
                       ? '1 URL was targeted, but no page results are included because the automated scan did not complete.'
                       : '1 key page was used in this report, reflecting the automated scan results for the provided URL.'}
                   </p>
               </section>
               <section>
                  <Search className="w-7 h-7 text-muted-foreground mb-5" />
                  <h3 className="text-lg font-medium mb-3">Automated checks</h3>
                  <p className="text-muted-foreground leading-relaxed">Findings are based on programmatic rules mapping to WCAG 2.1 guidelines.</p>
               </section>
               <section>
                  <Info className="w-7 h-7 text-muted-foreground mb-5" />
                  <h3 className="text-lg font-medium mb-3">Compliance disclaimer</h3>
                  <p className="text-muted-foreground leading-relaxed text-sm">{DISCLAIMER}</p>
               </section>
            </div>
         </div>

         {/* Failed State */}
         {isFailed && (
             <div className="report-page px-6 py-16 md:px-14 md:py-24">
               <div className="mb-8">
                  <XCircle className="w-16 h-16 text-destructive" />
               </div>
               <h2 className="text-3xl font-light tracking-tight mb-4" data-testid="text-scan-failed-title">Scan Did Not Complete</h2>
               <p className="text-destructive/80 text-lg leading-relaxed max-w-2xl mb-6" data-testid="text-scan-failed-desc">
                  {scan.errorMessage ?? 'An error occurred while scanning this site.'}
               </p>
               <p className="text-muted-foreground">
                  Please run a new scan from the dashboard once the issue has been resolved.
               </p>
            </div>
         )}

         {/* Success / No Findings State */}
         {!isFailed && !hasFindings && (
             <div className="report-page px-6 py-16 md:px-14 md:py-24">
               <div className="mb-8">
                  <CheckCircle2 className="w-16 h-16 text-emerald-600" />
               </div>
               <h2 className="text-3xl font-light tracking-tight mb-4" data-testid="text-no-issues-title">No Accessibility Issues Detected</h2>
               <p className="text-muted-foreground text-lg leading-relaxed max-w-2xl" data-testid="text-no-issues-desc">
                  The automated scan found no issues matching its rule set on this page. Note that automated scans cannot detect all potential accessibility barriers. Regular manual testing is recommended for comprehensive compliance.
               </p>
            </div>
         )}

         {/* Executive Summary */}
         {!isFailed && hasFindings && (
             <div id="executive-summary" className="report-page print-break-after-page px-6 py-16 md:px-14 md:py-24">
               <h2 className="text-2xl md:text-3xl font-light tracking-tight mb-12 border-b pb-6">Executive Summary</h2>

               {/* Metrics */}
               <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
                  <div className="p-8 border rounded-2xl bg-card shadow-sm" data-testid="metric-score">
                     <div className="flex items-center gap-3 mb-4 text-muted-foreground">
                        <Activity className="w-5 h-5" />
                        <span className="font-medium">Accessibility Score</span>
                     </div>
                      <div className="text-5xl font-light text-foreground">
                        {scan.score ?? '—'}
                        {scan.score !== null && <span className="text-base text-muted-foreground"> / 100</span>}
                      </div>
                  </div>
                  <div className="p-8 border rounded-2xl bg-card shadow-sm" data-testid="metric-total-issues">
                     <div className="flex items-center gap-3 mb-4 text-muted-foreground">
                         <AlertTriangle className="w-5 h-5" />
                         <span className="font-medium">Rules Flagged</span>
                     </div>
                     <div className="text-5xl font-light text-foreground">{findings.length}</div>
                  </div>
                   <div className="p-8 border rounded-2xl bg-card shadow-sm" data-testid="metric-total-instances">
                     <div className="flex items-center gap-3 mb-4 text-muted-foreground">
                         <Search className="w-5 h-5" />
                         <span className="font-medium">Instances Found</span>
                     </div>
                      <div className="text-5xl font-light text-foreground">{totalInstances}</div>
                  </div>
               </div>

                <div className="grid gap-10 md:grid-cols-[1.35fr_0.65fr]">
                  <section>
                    <h3 className="text-xl font-medium mb-7 text-foreground">Rules by Category</h3>
                    <div className="space-y-6">
                      {CATEGORY_ORDER.map(cat => {
                        const count = findingsByCategory[cat]?.length || 0;
                        const Icon = CATEGORY_ICONS[cat];
                        return (
                          <div key={cat} data-testid={`category-breakdown-${cat}`}>
                            <div className="mb-2 flex items-center justify-between gap-4 text-sm">
                              <div className="flex items-center gap-2 font-medium text-foreground">
                                <Icon className="h-4 w-4 text-muted-foreground" />
                                {CATEGORY_LABELS[cat]}
                              </div>
                              <span className="font-semibold text-foreground">{count}</span>
                            </div>
                            <div className="h-2.5 overflow-hidden rounded-full bg-muted">
                              <div
                                className="h-full rounded-full bg-[#243a5a]"
                                style={{ width: `${(count / maxCategoryCount) * 100}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>

                  <section>
                    <h3 className="text-xl font-medium mb-7 text-foreground">Priority</h3>
                    <div className="divide-y rounded-2xl border bg-card px-5">
                      {SEVERITY_ORDER.map(severity => {
                        const colors = SEVERITY_COLORS[severity];
                        const count = scan.issueCounts?.[severity] ?? 0;
                        return (
                          <div
                            key={severity}
                            className="flex items-center justify-between gap-4 py-4"
                            data-testid={`severity-breakdown-${severity}`}
                          >
                            <div className="flex items-center gap-3 text-sm font-medium">
                              <span
                                className="h-2.5 w-2.5 rounded-full"
                                style={{ backgroundColor: colors.text }}
                              />
                              {SEVERITY_LABELS[severity]}
                            </div>
                            <span className="text-lg font-semibold">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </section>
               </div>
            </div>
         )}

         {/* Detailed Findings */}
         {!isFailed && hasFindings && (
            <div className="findings-section">
               {sortedFindings.map((finding, idx) => {
                  const cat = finding.category ?? 'structure';
                  const Icon = CATEGORY_ICONS[cat];
                  const colors = SEVERITY_COLORS[finding.severity] ?? SEVERITY_COLORS.minor;

                  return (
                      <div
                        key={finding.rule}
                        id={`finding-${idx + 1}`}
                        className={`report-page px-6 py-16 md:px-14 md:py-24 ${idx < sortedFindings.length - 1 ? 'print-break-after-page' : ''}`}
                        data-testid={`finding-${idx}`}
                      >
                         <div className="mb-8 flex flex-wrap items-center justify-between gap-3 text-sm font-bold uppercase tracking-widest text-muted-foreground">
                            <span data-testid={`finding-counter-${idx}`}>Finding {idx + 1} of {sortedFindings.length}</span>
                            <span className="font-mono normal-case tracking-normal" data-testid={`finding-rule-${idx}`}>{finding.rule}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-6 items-start mb-10">
                           <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center shrink-0 border shadow-sm mt-1">
                              <Icon className="w-7 h-7 text-muted-foreground" />
                           </div>
                           <div className="flex-1">
                              <h3 className="text-2xl md:text-3xl font-light leading-snug text-foreground mb-5" data-testid={`finding-message-${idx}`}>
                                 {finding.message}
                              </h3>
                              <div className="flex flex-wrap gap-2">
                                 <span className="px-3 py-1 rounded-md text-xs font-bold border uppercase tracking-wider shadow-sm" style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }} data-testid={`finding-severity-${idx}`}>
                                    {SEVERITY_LABELS[finding.severity]}
                                 </span>
                                 <span className="px-3 py-1 rounded-md bg-secondary text-secondary-foreground text-xs font-bold border border-border uppercase tracking-wider shadow-sm" data-testid={`finding-category-${idx}`}>
                                    {CATEGORY_LABELS[cat]}
                                 </span>
                                 {finding.wcag && (
                                    <span className="px-3 py-1 rounded-md bg-muted text-muted-foreground text-xs font-bold border border-border uppercase tracking-wider shadow-sm" data-testid={`finding-wcag-${idx}`}>
                                       WCAG {finding.wcag}
                                    </span>
                                 )}
                              </div>
                           </div>
                        </div>

                        <div className="space-y-12">
                           <section>
                              <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Impact</h4>
                              <p className="text-foreground text-lg leading-relaxed max-w-4xl" data-testid={`finding-impact-${idx}`}>
                                 {CATEGORY_EXPLAINERS[cat]}
                              </p>
                           </section>

                           {finding.howToFix && (
                              <section>
                                 <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">How to Fix</h4>
                                 <div className="bg-muted/40 border border-border/60 p-6 rounded-2xl text-foreground text-lg leading-relaxed max-w-4xl shadow-sm" data-testid={`finding-fix-${idx}`}>
                                    {finding.howToFix}
                                 </div>
                              </section>
                           )}

                           <section className="print-break-inside-avoid">
                               <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-3">
                                 Instances Found
                                 <span className="bg-muted text-muted-foreground px-2.5 py-0.5 rounded-md text-xs border" data-testid={`finding-count-${idx}`}>{finding.count}</span>
                              </h4>
                               <div className="max-w-4xl">
                                  <div className="rounded-t-2xl border border-b-0 bg-muted/70 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                    Example HTML captured during the scan
                                  </div>
                                  <pre className="min-h-24 overflow-x-auto whitespace-pre-wrap break-all rounded-b-2xl border border-border/60 bg-muted/30 p-6 font-mono text-sm text-muted-foreground shadow-sm" data-testid={`finding-sample-${idx}`}>
                                    {finding.sample ?? 'No HTML sample was captured for this finding.'}
                                  </pre>
                               </div>
                           </section>
                        </div>
                     </div>
                  );
               })}
            </div>
         )}
      </div>

      <div className="print-footer">
        <div className="flex justify-between items-center w-full px-8">
          <div className="text-[#64748b] uppercase tracking-widest font-semibold text-[9px]">
             ADA Widget <span className="font-normal opacity-70 ml-2 border-l border-[#e2e8f0] pl-2">Accessibility Remediation Report</span>
          </div>
          <div className="text-[#64748b] text-[9px] uppercase tracking-widest">
             {site.domain} <span className="opacity-70 mx-2">·</span> {scanDate}
          </div>
        </div>
      </div>
    </div>
  );
}
